import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BarChart3,
  Bell,
  BookOpen,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  FileText,
  HelpCircle,
  LogOut,
  MessageSquare,
  Package,
  Search,
  User,
  Wallet as WalletIcon,
  X,
  type LucideIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { DocumentHead } from '@/components/layout/DocumentHead';
import { SkipLink } from '@/components/layout/SkipLink';
import { SearchModal } from '@/components/layout/SearchModal';
import { NotificationsPanel } from '@/components/layout/NotificationsPanel';
import { getNotifications, type NotificationItem } from '@/services/notificationService';
import { getWalletBalance } from '@/services/walletService';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { AuthenticatedImage } from '@/components/common/AuthenticatedImage';
import { useUser } from '@/stores/authStore';
import { useLogoutSync } from '@/hooks/useLogoutSync';
import { useHydrateAuth } from '@/hooks/useHydrateAuth';
import { clearAuthStorage } from '@/services/keycloakAuthService';
import { SessionTimer } from '@/components/common/SessionTimer';
import { GlobalLoadingBar } from '@/components/layout/GlobalLoadingBar';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { Footer } from '@/components/layout/Footer';
import { generateBreadcrumbs } from '@/utils/breadcrumbs';
import Frame from '@/imports/Frame-27-81';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * AppLayout — Phase-14 unified chrome.
 *
 * Structural shape (mirrors `src/demo/chrome/DemoChromeA.tsx`):
 *
 *   <div flex h-screen flex-col>
 *     <header sticky h-[64px]>
 *       [240px navy logo column]
 *       [white "rest" with toggle + breadcrumb left, session/bell/avatar right]
 *     </header>
 *     <div flex min-h-0 flex-1>
 *       <aside>           desktop: w-[240px] ↔ w-0 collapsible
 *                         mobile (< lg): fixed translate-x-* overlay
 *       <div flex-1>
 *         <main scrollable />
 *         <Footer />
 *       </div>
 *     </div>
 *   </div>
 *
 * Webapp pins `html { font-size: 14px }`, so every dimension here is in
 * absolute px (h-[64px], w-[240px], text-[14px]/[20px], px-[16px], …) per
 * the demo's convention — keeps the chrome pixel-identical with PE/admin.
 */

interface NavItemModel {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItemModel[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: BarChart3 },
  { id: 'browse-sites', label: 'Browse Sites', path: '/browse-sites', icon: Search },
  { id: 'packages', label: 'Packages', path: '/packages', icon: Package },
  { id: 'subscriptions', label: 'Subscriptions', path: '/subscriptions', icon: CreditCard },
  { id: 'orders', label: 'Orders', path: '/orders', icon: FileText },
  { id: 'content-orders', label: 'Content Orders', path: '/content-orders', icon: MessageSquare },
  { id: 'help-center', label: 'Help Center', path: '/help-center', icon: HelpCircle },
];

const scrollPositions = new Map<string, number>();

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const prevPathRef = useRef(location.pathname);

  // Modal / panel state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<NotificationItem[]>([]);

  // Sidebar / responsive state (ephemeral — no localStorage)
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  // Wallet balance for sidebar card
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  useLogoutSync();
  useHydrateAuth();

  // Window resize → re-derive mobile/sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initial wallet balance fetch
  useEffect(() => {
    let cancelled = false;
    getWalletBalance()
      .then((data) => {
        if (cancelled) return;
        const amount =
          typeof data.available_balance === 'number'
            ? data.available_balance
            : typeof data.balance === 'number'
              ? data.balance
              : typeof data.total_balance === 'number'
                ? data.total_balance
                : 0;
        setWalletBalance(amount);
      })
      .catch(() => {
        if (!cancelled) setWalletBalance(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Unread notifications: initial fetch + refetch on panel open
  useEffect(() => {
    let cancelled = false;
    getNotifications({ read: 'false', per_page: 20 })
      .then((res) => {
        if (!cancelled) setUnreadNotifications(res.notifications);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isNotificationsOpen) return;
    let cancelled = false;
    getNotifications({ read: 'false', per_page: 20 })
      .then((res) => {
        if (!cancelled) setUnreadNotifications(res.notifications);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isNotificationsOpen]);

  // Focus management — push focus into main content on route change
  useEffect(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) mainContent.focus();
  }, [location.pathname]);

  // Scroll restoration
  useEffect(() => {
    const prevPath = prevPathRef.current;
    if (prevPath !== location.pathname) {
      scrollPositions.set(prevPath, window.scrollY);
    }
    const savedPosition = scrollPositions.get(location.pathname);
    if (savedPosition !== undefined) {
      window.scrollTo(0, savedPosition);
      scrollPositions.delete(location.pathname);
    } else {
      window.scrollTo(0, 0);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // Auto-close mobile sidebar on route change
  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNotificationRead = (id: string) => {
    setUnreadNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setIsSidebarOpen((v) => !v), []);

  const handleLogout = () => {
    try {
      const loadingToast = toast.loading('Logging out...', { position: 'top-right' });
      clearAuthStorage();
      setTimeout(() => {
        toast.update(loadingToast, {
          render: 'Logged out successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 2000,
        });
        void navigate('/login');
      }, 1000);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Logout failed. Please try again.';
      toast.error(msg, { position: 'top-right' });
    }
  };

  const unreadCount = unreadNotifications.length;
  const formattedBalance = walletBalance === null ? '—' : `$${walletBalance.toFixed(2)}`;

  // Breadcrumb — render whatever `generateBreadcrumbs` returns. On
  // /dashboard that's just [Home] (rendered as a single active crumb);
  // on deeper routes it's [Home, …, Current].
  const breadcrumbItems = generateBreadcrumbs(location.pathname);
  const showBreadcrumb = breadcrumbItems.length > 0;

  const userInitials = `${user?.first_name?.[0]?.toUpperCase() ?? 'U'}${
    user?.last_name?.[0]?.toUpperCase() ?? 'S'
  }`;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-muted/30 font-sans">
      <GlobalLoadingBar />
      <SkipLink />
      <DocumentHead />

      {/* ─────────────── Header (full-width, 64px) ─────────────── */}
      <header className="sticky top-0 z-30 flex h-[64px] shrink-0 items-center bg-white">
        {/* Logo column — navy 240px, mirrors sidebar width */}
        <Link
          to="/dashboard"
          aria-label="DomainBoost home"
          className="flex h-full w-[240px] shrink-0 items-center gap-[12px] border-b border-white/10 bg-primary px-[20px] text-white transition-colors hover:bg-primary/90"
        >
          <div className="relative h-[30px] w-[48px] shrink-0">
            <Frame />
          </div>
          <div className="min-w-0 leading-tight">
            <span className="block truncate font-display text-[14px]/[18px] font-bold">
              DomainBoost
            </span>
            <span className="block truncate text-[10px]/[13px] font-normal text-white/70">
              Web Application
            </span>
          </div>
        </Link>

        {/* Rest of header (white) */}
        <div className="flex h-full flex-1 items-center justify-between border-b border-border px-[16px]">
          {/* Left: toggle + breadcrumb */}
          <div className="flex items-center gap-[12px]">
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              aria-expanded={isSidebarOpen}
              className="rounded-[12px] border border-border p-[8px] text-muted-foreground transition-colors duration-300 hover:bg-muted hover:text-foreground"
            >
              {isSidebarOpen ? (
                <ChevronsLeft className="h-[18px] w-[18px]" aria-hidden />
              ) : (
                <ChevronsRight className="h-[18px] w-[18px]" aria-hidden />
              )}
            </button>

            {showBreadcrumb && (
              <nav aria-label="Breadcrumb" className="hidden sm:block">
                <ol className="flex items-center gap-[6px] text-[12px] text-muted-foreground">
                  {breadcrumbItems.map((item, index) => (
                    <React.Fragment key={item.path}>
                      {index > 0 && (
                        <li aria-hidden className="text-muted-foreground/40">
                          /
                        </li>
                      )}
                      <li>
                        {item.isCurrentPage ? (
                          <span aria-current="page" className="text-foreground/70">
                            {item.label}
                          </span>
                        ) : (
                          <Link
                            to={item.path}
                            className="transition-colors duration-300 hover:text-foreground"
                          >
                            {item.label}
                          </Link>
                        )}
                      </li>
                    </React.Fragment>
                  ))}
                </ol>
              </nav>
            )}
          </div>

          {/* Right cluster: session chip, notifications, avatar */}
          <div className="flex items-center gap-[8px]">
            <SessionTimer />

            <NotificationsPanel
              isOpen={isNotificationsOpen}
              onOpenChange={setIsNotificationsOpen}
              notifications={unreadNotifications}
              onNotificationRead={handleNotificationRead}
              trigger={
                <button
                  type="button"
                  aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                  className="relative rounded-[12px] p-[8px] text-muted-foreground transition-colors duration-300 hover:bg-muted hover:text-foreground"
                >
                  <Bell className="h-[18px] w-[18px]" aria-hidden />
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-[4px] text-[10px] leading-[16px] font-bold"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </button>
              }
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-[8px] rounded-[12px] p-[6px] transition-colors duration-300 hover:bg-muted"
                  aria-label={`Account menu for ${user?.full_name ?? user?.first_name ?? 'user'}`}
                >
                  <Avatar className="h-[32px] w-[32px]">
                    {user?.avatar_url ? (
                      <AuthenticatedImage
                        src={user.avatar_url}
                        alt={user?.full_name ?? 'User'}
                        className="aspect-square size-full rounded-full object-cover"
                      />
                    ) : null}
                    <AvatarFallback className="bg-primary text-[12px]/[16px] font-semibold text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden text-left leading-tight md:block">
                    <span className="block text-[14px] font-semibold text-foreground">
                      {user?.full_name ?? user?.first_name ?? 'User'}
                    </span>
                    <span className="block text-[11px] font-normal text-muted-foreground">
                      {user?.email ?? 'user@domainboost.io'}
                    </span>
                  </span>
                  <ChevronDown
                    className="hidden h-[16px] w-[16px] text-muted-foreground md:block"
                    aria-hidden
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[224px] !rounded-[12px] p-[4px]">
                <DropdownMenuItem
                  className="gap-[8px] px-[8px] py-[6px] text-[14px]"
                  onClick={() => void navigate('/profile')}
                >
                  <User aria-hidden className="size-[16px]" />
                  Profile &amp; Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-[8px] px-[8px] py-[6px] text-[14px]"
                  onClick={() => void navigate('/wallet')}
                >
                  <WalletIcon aria-hidden className="size-[16px]" />
                  Wallet
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-[8px] px-[8px] py-[6px] text-[14px]"
                  onClick={() => void navigate('/notifications')}
                >
                  <Bell aria-hidden className="size-[16px]" />
                  Notifications
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-[8px] px-[8px] py-[6px] text-[14px]"
                  onClick={() => void navigate('/help-articles')}
                >
                  <BookOpen aria-hidden className="size-[16px]" />
                  Help Articles
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-[8px] px-[8px] py-[6px] text-[14px]"
                  onClick={() => void navigate('/faqs')}
                >
                  <HelpCircle aria-hidden className="size-[16px]" />
                  FAQs
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  variant="destructive"
                  className="gap-[8px] px-[8px] py-[6px] text-[14px]"
                  onClick={handleLogout}
                >
                  <LogOut aria-hidden className="size-[16px]" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* ─────────────── Body row: sidebar + content ─────────────── */}
      <div className="flex min-h-0 flex-1">
        {/* Mobile overlay backdrop — sits under the 64px header */}
        {isMobile && isSidebarOpen && (
          <div
            className="fixed inset-x-0 bottom-0 top-[64px] z-30 bg-black/50 lg:hidden"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}

        {/* Sidebar — desktop in-flow collapse, mobile fixed translate overlay */}
        <aside
          aria-label="Primary navigation"
          aria-hidden={!isSidebarOpen}
          className={
            isMobile
              ? `fixed bottom-0 left-0 top-[64px] z-40 flex w-[240px] shrink-0 flex-col bg-primary text-white shadow-2xl transition-transform duration-300 ease-in-out lg:hidden ${
                  isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`
              : `hidden shrink-0 flex-col bg-primary text-white transition-[width] duration-300 lg:flex ${
                  isSidebarOpen ? 'w-[240px]' : 'w-0 overflow-hidden'
                }`
          }
        >
          {/* Mobile close button */}
          {isMobile && (
            <button
              type="button"
              onClick={closeSidebar}
              aria-label="Close sidebar"
              className="absolute right-[12px] top-[12px] rounded-[8px] p-[6px] text-white/80 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <X className="h-[18px] w-[18px]" aria-hidden />
            </button>
          )}

          {/* Wallet balance card */}
          <div className="px-[16px] pt-[16px]">
            <Link
              to="/wallet"
              onClick={() => isMobile && closeSidebar()}
              className="block rounded-[12px] border border-white/15 bg-white/5 p-[16px] transition-colors duration-300 hover:bg-white/10"
            >
              <div className="flex items-center gap-[8px] text-[12px]/[16px] font-medium text-white/70">
                <WalletIcon className="h-[16px] w-[16px]" aria-hidden />
                Wallet Balance
              </div>
              <p className="mt-[4px] font-display text-[24px]/[32px] font-bold">
                {formattedBalance}
              </p>
              <p className="mt-[2px] text-[11px] uppercase tracking-wider text-white/50">
                Click to add
              </p>
            </Link>
          </div>

          {/* Nav — scrollbar hidden (matches demo). Real NavLink active state. */}
          <nav
            aria-label="Main"
            className="flex-1 overflow-y-auto px-[12px] py-[16px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            <ul className="space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id} className="relative">
                    <NavLink
                      to={item.path}
                      onClick={() => isMobile && closeSidebar()}
                      end={item.path === '/dashboard'}
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-[12px] rounded-[12px] border px-[12px] py-[10px] text-[14px] font-medium transition-colors duration-300',
                          isActive
                            ? 'border-white/15 bg-white/[0.12] text-white'
                            : 'border-transparent text-white/75 hover:bg-white/10 hover:text-white',
                        ].join(' ')
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span
                              aria-hidden
                              className="absolute left-0 top-1/2 h-[20px] w-[3px] -translate-y-1/2 rounded-r-[6px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                            />
                          )}
                          <Icon className="h-[20px] w-[20px] shrink-0" aria-hidden />
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout pinned to bottom */}
          <div className="border-t border-white/10 p-[12px]">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-[12px] rounded-[12px] px-[12px] py-[10px] text-[14px]/[20px] font-medium text-white/75 transition-colors duration-300 hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-[20px] w-[20px] shrink-0" aria-hidden />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Scrollable content column — main + footer scroll together */}
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>

          <Footer />
        </div>
      </div>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <ChatWidget />
    </div>
  );
};

export default AppLayout;
